import { Component, OnInit, OnDestroy, Inject, ViewChild, ElementRef, HostListener } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ReportsService } from '@app-reports/services/reports/reports.service';
import { CustomReportsResponse } from '@app-reports/models/reports.model';
import { API } from '@app-core/constants/api.constants';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DurationRange } from '@app-core/models/core.model';
import { Store } from '@ngrx/store';
import { getDurationFilterState } from '@app-home/reducers';
import { TALK_TO_DATA_DATE_RANGES, TALK_TO_DATA_PREDEFINED_QUESTIONS } from '@app-reports/constants/reports.constants';
import { TranslateService } from '@ngx-translate/core';
import { AccessService } from '@app-core/services/access/access.service';
import { TaggingService } from '@app-asset-config/services/tagging.service';
import { DataService } from '@app-core/services/data/data.service';

interface ChatMessage {
  type: 'user' | 'bot' | 'sql';
  content: string;
  timestamp: Date;
  isQuestion?: boolean;
  isPeriodSelection?: boolean;
  isTagSelection?: boolean;
  selectedPeriod?: string;
  sqlData?: string;
  showSqlExpanded?: boolean;
}

export interface Metadata {
  date_range: string;
  sql_where_date_columns: string[];
  tag_ids: number[];
}

interface Tag {
  tagId: number;
  tagName: string;
  attributeId?: number;
  attributeName?: string;
  attributeType?: string;
  id?: number;
  name?: string;
}
interface ReportDataRow {
  [key: string]: string | number | boolean | null | undefined;
}

@Component({
  selector: 'app-talk-to-data-modal',
  templateUrl: './talk-to-data-modal.component.html',
  styleUrls: ['./talk-to-data-modal.component.scss'],
})
export class TalkToDataModalComponent implements OnInit, OnDestroy {
  @ViewChild('chatContainer') private chatContainer: ElementRef;
  public tripDetailsForm: FormGroup;
  public unSelectAllTag = false;
  public entityType = ['asset', 'driver'];
  public displayedColumns: string[] = [];
  public dataSource: ReportDataRow[] = [];
  public chatMessages: ChatMessage[] = [];
  public showNoDataMessage: boolean = true;
  public isLoading: boolean = false;
  public currentQuestion: string = '';
  public currentAnswer: string = '';
  public sessionId: string = '';
  public selectedTagIds: number[] = [];
  public filterSelectedTagIds: number[] = [];
  public selectedTagNames: string[] = [];
  public showNoDataRetrySection: boolean = false;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public predefinedQuestions: string[] = TALK_TO_DATA_PREDEFINED_QUESTIONS;
  public conversationState: 'initial' | 'awaiting_period' | 'awaiting_tags' | 'report_generated' = 'initial';
  public reportPeriods = TALK_TO_DATA_DATE_RANGES;
  public selectedPeriod: string = 'Past 7 days';
  public isFirstQuery: boolean = true;
  public showSaveReportModal = false;
  private clientId: string;
  private fleetId: string;
  private destroy$ = new Subject<void>();
  public sql: string[];
  public executedSql: string[];
  public generatedReportInfo: Object;
  public form = null;
  public durationRange: DurationRange;
  public reportName: string = '';
  public reportDescription: string = '';
  public durationControl: FormControl;
  public showDateRangeOptions: boolean = false;
  public selectedStartDate: Date | null = null;
  public selectedEndDate: Date | null = null;
  public csvLoader: boolean;
  public saveReportLoader: boolean;
  public csvDownloadSuccess: boolean;
  public getCustomReportsListData: () => void;
  public displaySql: string;
  public sqlLoader: boolean;
  public userProfileIcon: string;
  public loginName = '';
  public sqlData: string;
  public showSqlThinking: boolean = false;
  public metadata: Metadata;
  public userTags: Tag[] = [];
  public attributeList = [];
  private allTags: Tag[] = [];
  public assignedTagList: number[] = [];
  private lastUsedPeriod: string = '';
  private lastUsedTagIds: number[] = [];
  private lastUsedTagNames: string[] = [];
  public tagsInitialized: boolean = false;
  private pendingTagSelection: number[] | null = null;
  public sql_statements: string[];

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private reportService: ReportsService,
    private snackBarService: SnackBarService,
    private translate: TranslateService,
    private accessService: AccessService,
    private taggingService: TaggingService,
    private dialogRef: MatDialogRef<TalkToDataModalComponent>,
    public dataService: DataService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<any>
  ) {}

  ngOnInit() {
    const { loginName = '' } = this.accessService.getLoginInfo();
    this.clientId = this.data?.clientId;
    this.fleetId = this.data?.fleetId;
    this.loginName = loginName;
    this.userProfileIcon = this.loginName.slice(0, 2);
    this.getCustomReportsListData = this.data?.getCustomReportsListData;
    this.durationControl = new FormControl(null);
    this.getAttributeList();
    this.tripDetailsForm = this.fb.group({
      startDate: [null],
      endDate: [null],
      customQuestion: [''],
    });

    this.selectedPeriod = 'Past 7 days';
    this.calculateDateRange('Past 7 days');

    this.store
      .select(getDurationFilterState)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((durationFilter: any) => {
        const { days, displayStartDate: startDate, displayEndDate: endDate } = durationFilter;
        this.durationRange = { days, startDate, endDate };
        if (this.form) {
          this.form.patchValue({
            ...this.durationRange,
            tagIds: durationFilter.tagIds,
          });
        }
      });
    this.addBotMessage(this.translate.instant('You can select a predefined question or type your own.'));
  }

  ngAfterViewInit() {
    setTimeout(() => this.scrollToBottom(), 100);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private scrollToBottom(): void {
    try {
      if (this.chatContainer) {
        this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.error('Scroll error:', err);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const isInsideDateRange = target.closest('.date-range-wrapper');
    if (!isInsideDateRange) {
      this.showDateRangeOptions = false;
    }
  }

  selectDateRangeFromFilter(days: string) {
    this.selectedPeriod = days;
    this.executeQuery(this.currentQuestion);
  }

  private calculateDateRange(range: string) {
    const today = new Date();
    this.selectedEndDate = new Date(today);

    switch (range) {
      case 'Past 3 days':
        this.selectedStartDate = new Date(today.setDate(today.getDate() - 3));
        break;
      case 'Past 7 days':
        this.selectedStartDate = new Date(today.setDate(today.getDate() - 7));
        break;
      case 'Past 14 days':
        this.selectedStartDate = new Date(today.setDate(today.getDate() - 14));
        break;
      case 'Past 30 days':
        this.selectedStartDate = new Date(today.setDate(today.getDate() - 30));
        break;
      default:
        this.selectedStartDate = new Date(today.setDate(today.getDate() - 30));
    }
  }

  getDateRangeDisplay(): string {
    if (this.selectedPeriod) {
      return this.selectedPeriod;
    }
    return '';
  }

  toggleDateRangeOptions() {
    this.showDateRangeOptions = !this.showDateRangeOptions;
  }

  onDateSelected(date: Date) {
    this.selectedEndDate = date;
  }

  private addUserMessage(content: string): void {
    this.chatMessages.push({
      type: 'user',
      content,
      timestamp: new Date(),
    });
    setTimeout(() => this.scrollToBottom(), 100);
  }

  private addBotMessage(
    content: string,
    options?: {
      isQuestion?: boolean;
      isPeriodSelection?: boolean;
      isTagSelection?: boolean;
    }
  ): void {
    this.chatMessages.push({
      type: 'bot',
      content,
      timestamp: new Date(),
      ...options,
    });
    setTimeout(() => this.scrollToBottom(), 100);
  }

  checkIfTouchDevice(): boolean {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-ignore
      navigator.msMaxTouchPoints > 0
    );
  }

  public clearSearchForm() {
    this.unSelectAllTag = true;
    this.selectedTagIds = [];
    this.filterSelectedTagIds = [];
    this.selectedTagNames = [];
    this.assignedTagList = [];
    this.userTags = this.getTagObjectsByIds(this.lastUsedTagIds) || [];
  }
  private getAttributeList() {
    const params = {
      limit: 100,
      offset: 0,
    };
    this.taggingService
      .getAttributes(params)
      .pipe(
        finalize(() => {}),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: any = {}) => {
          this.attributeList = res?.data;
          this.allTags = this.extractAllTags(this.attributeList);
          this.initializePreAssignedTags();
        },
        () => {}
      );
  }

  private extractAllTags(attributes: any[]): any[] {
    if (!attributes || !Array.isArray(attributes)) {
      return [];
    }
    const allTags = [];
    attributes.forEach((attribute) => {
      if (attribute.tags && Array.isArray(attribute.tags)) {
        attribute.tags.forEach((tag) => {
          allTags.push({
            ...tag,
            attributeId: attribute.attributeId,
            attributeName: attribute.attributeName,
            attributeType: attribute.attributeType,
          });
        });
      }
    });
    return allTags;
  }

  private getTagNamesByIds(tagIds: number[]): string[] {
    if (!tagIds || tagIds.length === 0) {
      return [];
    }
    const tagNames: string[] = [];
    tagIds.forEach((tagId) => {
      const foundTag = this.allTags.find((tag) => tag.tagId === tagId);
      if (foundTag && foundTag.tagName) {
        tagNames.push(foundTag.tagName);
      }
    });
    if (tagNames.length === tagIds.length) {
      return tagNames;
    }
    const remainingTagIds = tagIds.filter((id) => !this.allTags.some((tag) => tag.tagId === id));
    remainingTagIds.forEach((tagId) => {
      if (this.userTags && Array.isArray(this.userTags)) {
        const foundTag = this.userTags.find((tag) => tag.id === tagId || tag.tagId === tagId);
        if (foundTag && foundTag.name) {
          tagNames.push(foundTag.name);
        }
      }
    });
    return tagNames;
  }
  private initializePreAssignedTags() {
    const { fleets = [] } = this.accessService.getLoginInfo();
    const currentFleetInfo = fleets.find((x: any) => x.fleetId === this.fleetId);

    if (currentFleetInfo?.userTags && currentFleetInfo.userTags.length > 0) {
      const preAssignedTagIds = currentFleetInfo.userTags.map((tag) => tag.tagId);
      this.selectedTagIds = [...preAssignedTagIds];
      this.filterSelectedTagIds = [...preAssignedTagIds];
      this.lastUsedTagIds = [...preAssignedTagIds];
      this.selectedTagNames = this.getTagNamesByIds(preAssignedTagIds);
      this.lastUsedTagNames = [...this.selectedTagNames];
      this.userTags = this.getTagObjectsByIds(preAssignedTagIds);
      this.assignedTagList = [...preAssignedTagIds];
    } else {
      this.selectedTagIds = [];
      this.filterSelectedTagIds = [];
      this.lastUsedTagIds = [];
      this.selectedTagNames = [];
      this.lastUsedTagNames = [];
      this.userTags = [];
      this.assignedTagList = [];
    }
    this.tagsInitialized = true;
  }

  public selectedTags(tags: number[]) {
    this.selectedTagIds = tags;
    this.filterSelectedTagIds = tags;
    this.assignedTagList = this.selectedTagIds;
    this.selectedTagNames = this.getTagNamesByIds(this.selectedTagIds);
  }

  public selectedTagsRightSection(tags: number[]) {
    this.pendingTagSelection = [...tags];
    this.filterSelectedTagIds = [...tags];
    this.selectedTagNames = this.getTagNamesByIds(tags);
  }

  public onTagDropdownClosed() {
    if (this.pendingTagSelection !== null) {
      const previousTagIds = [...this.selectedTagIds];
      const newTagIds = [...this.pendingTagSelection];
      const tagsChanged = JSON.stringify(previousTagIds.sort((a, b) => a - b)) !== JSON.stringify(newTagIds.sort((a, b) => a - b));

      if (tagsChanged && this.currentQuestion && this.conversationState === 'report_generated') {
        this.selectedTagIds = [...newTagIds];
        this.assignedTagList = [...newTagIds];
        this.selectedTagNames = this.getTagNamesByIds(newTagIds);
        this.userTags = this.getTagObjectsByIds(newTagIds);
        if (newTagIds.length === 0) {
          this.unSelectAllTag = true;
          setTimeout(() => {
            this.unSelectAllTag = false;
          }, 100);
        }
        this.executeQuery(this.currentQuestion);
      }
      this.pendingTagSelection = null;
    }
  }

  public onPredefinedQuestionClick(question: string) {
    if (!this.isFirstQuery) {
      return;
    }
    if (this.conversationState !== 'initial') {
      return;
    }
    this.currentQuestion = question;
    this.addUserMessage(question);
    this.conversationState = 'awaiting_period';
    this.addBotMessage(this.translate.instant('Just to confirm before generating the report please select a period and related tags:'), {
      isPeriodSelection: true,
    });
  }

  public onReportPeriodSelect(period: string): void {
    this.selectedPeriod = period;
  }

  public onConfirmPeriodAndTags(): void {
    if (!this.selectedPeriod) {
      this.snackBarService.failure(this.translate.instant('Please select a period'));
      return;
    }
    if (this.conversationState !== 'awaiting_period') {
      return;
    }
    const tagsToUse = this.filterSelectedTagIds && this.filterSelectedTagIds.length > 0 ? this.filterSelectedTagIds : this.selectedTagIds;
    this.selectedTagIds = [...tagsToUse];
    this.selectedTagNames = this.getTagNamesByIds(this.selectedTagIds);
    this.userTags = this.getTagObjectsByIds(this.selectedTagIds);
    const tagText = this.selectedTagNames.length > 0 ? this.selectedTagNames.join(', ') : this.translate.instant('No tags selected');
    const summaryMessage = `${this.selectedPeriod}, ${tagText}`;
    this.addUserMessage(summaryMessage);
    this.executeQuery(this.currentQuestion);
    this.conversationState = 'report_generated';
  }

  public onRetryWithNewFilters(): void {
    if (!this.selectedPeriod && this.lastUsedPeriod) {
      this.selectedPeriod = this.lastUsedPeriod;
    }

    if (!this.selectedPeriod) {
      this.snackBarService.failure(this.translate.instant('Please select a period'));
      return;
    }

    if (this.filterSelectedTagIds && this.filterSelectedTagIds.length > 0) {
      this.selectedTagIds = [...this.filterSelectedTagIds];
    }
    this.selectedTagNames = this.getTagNamesByIds(this.selectedTagIds);
    this.userTags = this.getTagObjectsByIds(this.selectedTagIds);
    const tagText = this.selectedTagNames.length > 0 ? this.selectedTagNames.join(', ') : this.translate.instant('No tags selected');
    const summaryMessage = `${this.selectedPeriod}, ${tagText}`;
    this.addUserMessage(summaryMessage);
    this.showNoDataRetrySection = false;
    this.executeQuery(this.currentQuestion);
  }

  public onCustomQuestionSubmit(): void {
    const typedQuestion = this.tripDetailsForm.get('customQuestion')?.value?.trim();
    if (this.isFirstQuery && this.conversationState === 'initial') {
      if (!typedQuestion) {
        this.snackBarService.failure(this.translate.instant('Please enter a question'));
        return;
      }
      this.currentQuestion = typedQuestion;
      this.addUserMessage(typedQuestion);
      this.conversationState = 'awaiting_period';
      this.addBotMessage(
        this.translate.instant('Just to confirm before generating the report preview, what period would you like for this report?'),
        {
          isPeriodSelection: true,
        }
      );
      this.tripDetailsForm.get('customQuestion')?.setValue('');
      return;
    }

    if (this.isFirstQuery && this.conversationState === 'awaiting_period') {
      if (!typedQuestion) {
        this.snackBarService.failure(this.translate.instant('Please select a period and click confirm'));
        return;
      }
      this.addUserMessage(typedQuestion);
      this.addBotMessage(this.translate.instant('Please select a period from the dropdown and click confirm to continue.'));
      this.tripDetailsForm.get('customQuestion')?.setValue('');
      return;
    }

    if (!this.isFirstQuery && this.conversationState === 'report_generated') {
      if (!typedQuestion) {
        this.snackBarService.failure(this.translate.instant('Please enter a question'));
        return;
      }
      if (!this.selectedPeriod) {
        this.snackBarService.failure(this.translate.instant('Please ensure period is selected'));
        return;
      }
      this.currentQuestion = typedQuestion;
      this.addUserMessage(typedQuestion);
      this.executeQuery(typedQuestion);
      this.tripDetailsForm.get('customQuestion')?.setValue('');
      return;
    }
  }

  public toggleSqlThinkingForMessage(index: number): void {
    if (this.chatMessages[index] && this.chatMessages[index].type === 'sql') {
      this.chatMessages[index].showSqlExpanded = !this.chatMessages[index].showSqlExpanded;
    }
  }

  private executeQuery(question: string) {
    if (!this.selectedPeriod) {
      this.snackBarService.failure(this.translate.instant('Please select a report period'));
      return;
    }
    this.lastUsedPeriod = this.selectedPeriod;
    this.lastUsedTagIds = [...this.selectedTagIds];
    this.lastUsedTagNames = [...this.selectedTagNames];
    this.isLoading = true;
    this.showNoDataMessage = false;
    this.showNoDataRetrySection = false;
    this.dataSource = [];
    this.displayedColumns = [];
    const requestBody: any = {
      question: question,
      clientId: this.clientId,
      fleetId: this.fleetId,
      dateRange: this.selectedPeriod,
      tagIds: this.selectedTagIds.length > 0 ? this.selectedTagIds : [],
    };

    if (this.sessionId) {
      requestBody.sessionId = this.sessionId;
    }

    this.reportService
      .getCustomReports(requestBody)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (response) => {
          if (response?.sessionId) {
            this.sessionId = response.sessionId;
          }
          this.sql = response?.sql_statements || [];
          this.executedSql = response?.executed_sql_statements || [];
          this.sql_statements = response?.sql_statements || [];
          this.metadata = response?.metadata || { date_range: '', sql_where_date_columns: [], tag_ids: [] };
          if (response.sql_statements?.[0]) {
            this.addSqlThinkingMessage(response.sql_statements[0].trim());
          }
          this.processResponse(response);
          if (this.isFirstQuery) {
            this.isFirstQuery = false;
          }
          this.conversationState = 'report_generated';
        },
        error: (err) => {
          console.error('API Error:', err);
          this.handleError(err);
          if (this.isFirstQuery) {
            this.isFirstQuery = false;
          }
          this.conversationState = 'report_generated';
        },
      });
  }

  private addSqlThinkingMessage(sqlData: string): void {
    this.chatMessages.push({
      type: 'sql',
      content: '',
      timestamp: new Date(),
      sqlData: sqlData,
      showSqlExpanded: false,
    });
    setTimeout(() => this.scrollToBottom(), 100);
  }

  private processResponse(response: CustomReportsResponse): void {
    if (!response) {
      this.handleNoData();
      return;
    }
    if (response.sessionId) {
      this.sessionId = response.sessionId;
    }
    this.sql = response.sql_statements || [];
    this.currentAnswer = response.answer || '';
    this.addBotMessage(this.currentAnswer);
    if (response.data?.[0] && Array.isArray(response.data[0]) && response.data[0].length > 0) {
      const tableData = response.data[0];
      const isMeaningfulData = this.checkIfMeaningfulData(tableData);
      if (!isMeaningfulData) {
        this.handleNoData();
        return;
      }
      const processedData = tableData.map((row) => {
        const newRow = { ...row };
        if ('Average Trip Duration (sec)' in newRow && typeof newRow['Average Trip Duration (sec)'] === 'number') {
          newRow['Average Trip Duration (sec)'] = Math.round(newRow['Average Trip Duration (sec)'] * 100) / 100;
        }
        return newRow;
      });

      this.displayedColumns = Object.keys(processedData[0]);
      this.dataSource = processedData;
      this.showNoDataMessage = false;
      this.showNoDataRetrySection = false;
      this.snackBarService.success(this.translate.instant('Report generated successfully'));
      this.addBotMessage(
        this.translate.instant('Report generated! You can view the preview on the right panel. Feel free to ask another question.')
      );
    } else {
      this.handleNoData();
    }
  }

  private checkIfMeaningfulData(tableData: any[]): boolean {
    if (tableData.length === 1) {
      const row = tableData[0];
      const values = Object.values(row);
      return values.some((value) => {
        if (value === null || value === undefined || value === '') {
          return false;
        }
        if (typeof value === 'number' && value === 0) {
          return false;
        }
        return true;
      });
    }
    return tableData.length > 1;
  }

  private handleNoData(): void {
    this.showNoDataMessage = true;
    this.showNoDataRetrySection = true;
    this.dataSource = [];
    this.displayedColumns = [];
    if (this.lastUsedTagIds.length > 0) {
      this.selectedTagIds = [...this.lastUsedTagIds];
      this.assignedTagList = this.getTagObjectsByIds(this.lastUsedTagIds);
      this.selectedTagNames = [...this.lastUsedTagNames];
      this.userTags = this.getTagObjectsByIds(this.lastUsedTagIds);
    }
    if (this.lastUsedTagIds.length === 0) {
      this.selectedTagIds = [];
      this.selectedTagNames = [];
      this.assignedTagList = [];
      this.userTags = [];
    }
    if (this.lastUsedPeriod) {
      this.selectedPeriod = this.lastUsedPeriod;
    }
    this.addBotMessage(
      this.translate.instant('Currently there is no activity found for the selected values, kindly select a different value and try again:')
    );
  }

  private getTagObjectsByIds(tagIds: number[]): any[] {
    if (!tagIds || tagIds.length === 0) {
      return [];
    }
    const tagObjects: any[] = [];
    tagIds.forEach((tagId) => {
      const foundTag = this.allTags.find((tag) => tag.tagId === tagId);
      if (foundTag) {
        tagObjects.push(foundTag);
      }
    });
    const remainingTagIds = tagIds.filter((id) => !this.allTags.some((tag) => tag.tagId === id));
    remainingTagIds.forEach((tagId) => {
      if (this.userTags && Array.isArray(this.userTags)) {
        const foundTag = this.userTags.find((tag) => tag.id === tagId || tag.tagId === tagId);
        if (foundTag) {
          tagObjects.push({
            tagId: foundTag.id || foundTag.tagId,
            tagName: foundTag.name || foundTag.tagName,
            ...foundTag,
          });
        }
      }
    });
    return tagObjects;
  }

  private handleError(error: any): void {
    this.showNoDataMessage = true;
    this.showNoDataRetrySection = true;
    this.dataSource = [];
    this.displayedColumns = [];
    if (this.lastUsedTagIds.length > 0) {
      this.selectedTagIds = [...this.lastUsedTagIds];
      this.assignedTagList = this.getTagObjectsByIds(this.lastUsedTagIds);
      this.selectedTagNames = [...this.lastUsedTagNames];
      this.userTags = this.getTagObjectsByIds(this.lastUsedTagIds);
    }
    if (this.lastUsedTagIds.length === 0) {
      this.selectedTagIds = [];
      this.selectedTagNames = [];
      this.assignedTagList = [];
      this.userTags = [];
    }

    if (this.lastUsedPeriod) {
      this.selectedPeriod = this.lastUsedPeriod;
    }
    let errorMessage = this.translate.instant('Failed to generate report. Please try again.');
    if (error.status === 400) {
      errorMessage = this.translate.instant('Invalid request. Please check your query and try again.');
    } else if (error.status === 401) {
      errorMessage = this.translate.instant('Unauthorized. Please log in again.');
    } else if (error.status === 404) {
      errorMessage = this.translate.instant('Service not found. Please contact support.');
    } else if (error.status === 500) {
      errorMessage = this.translate.instant('Unable to fetch data.');
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    }
    this.addBotMessage(errorMessage);
    this.snackBarService.failure(errorMessage);
  }

  public downloadReport() {
    this.csvLoader = true;
    const requestBody = {
      ...(this.sessionId && { sessionId: this.sessionId }),
      ...(this.sql_statements && { sql: this.sql_statements[0] }),
    };
    this.http
      .post(API.POST_GENERATE_REPORT_NAME, requestBody)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response: any) => {
          const reportName = response.report_name || '';
          this.downloadReportWithName(reportName);
        },
        error: (err) => {
          console.error('Error fetching report name:', err);
          this.csvLoader = false;
          this.snackBarService.failure(this.translate.instant('Failed to generate report name. Please try again.'));
        },
      });
  }

  private downloadReportWithName(reportName: string) {
    const params = {
      fleetId: this.fleetId,
      reportQuery: this.executedSql[0],
      reportType: 'CUSTOM_REPORT',
      reportName: reportName,
    };
    this.reportService
      .downloadCustomReport(params)
      .pipe(
        finalize(() => {
          this.csvLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        () => {
          this.csvDownloadSuccess = true;
          this.snackBarService.success(this.translate.instant('Request created successfully'));
        },
        (err: HttpErrorResponse) => {
          this.csvDownloadSuccess = false;
          if (err?.status === 409) {
            this.snackBarService.failure(this.translate.instant('tripListComponentTripDuplicate'));
          } else {
            this.snackBarService.failure(this.translate.instant('tripListComponentTripsDownloadFailure'));
          }
        }
      );
  }

  public openSaveReportModal(): void {
    if (this.dataSource.length === 0) {
      this.snackBarService.failure(this.translate.instant('No data available to save'));
      return;
    }
    this.saveReportLoader = true;
    const requestBody = {
      ...(this.sessionId && { sessionId: this.sessionId }),
      ...(this.sql_statements && { sql: this.sql_statements[0] }),
    };
    this.http
      .post(API.POST_GENERATE_REPORT_NAME, requestBody)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.saveReportLoader = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          this.reportName = response.report_name || '';
          const baseDescription = response.report_description || '';
          const today = new Date();
          const day = String(today.getDate()).padStart(2, '0');
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const year = today.getFullYear();
          const formattedDate = `${day}.${month}.${year}`;
          const tagNamesText = this.selectedTagNames.length > 0 ? this.selectedTagNames.join(', ') : 'No tags';
          this.reportDescription = `${baseDescription} Created on ${formattedDate} with ${tagNamesText}.`;
          this.showSaveReportModal = true;
        },
        error: (err) => {
          console.error('Error fetching report name:', err);
          this.snackBarService.failure(this.translate.instant('Failed to generate report name. Please try again.'));
        },
      });
  }

  public onReportSaved(): void {
    this.showSaveReportModal = false;
    this.dialogRef.close();
  }

  public isAskButtonDisabled(): boolean {
    const typedQuestion = this.tripDetailsForm.get('customQuestion')?.value?.trim();
    if (this.isLoading) {
      return true;
    }
    if (typedQuestion) {
      if (this.isFirstQuery && this.conversationState === 'awaiting_period') {
        return true;
      }
      return false;
    }
    return true;
  }

  public startNewChat(): void {
    this.currentQuestion = '';
    this.currentAnswer = '';
    this.sessionId = '';
    this.selectedPeriod = 'Past 7 days';
    this.conversationState = 'initial';
    this.chatMessages = [];
    this.isFirstQuery = true;
    this.showNoDataMessage = true;
    this.showNoDataRetrySection = false;
    this.sqlData = '';
    this.dataSource = [];
    this.displayedColumns = [];
    this.showSqlThinking = false;
    this.pendingTagSelection = null;
    this.sql = [];
    this.executedSql = [];
    this.metadata = { date_range: '', sql_where_date_columns: [], tag_ids: [] };
    this.showSaveReportModal = false;
    this.lastUsedPeriod = '';
    this.lastUsedTagIds = [];
    this.lastUsedTagNames = [];
    this.tagsInitialized = false;
    this.selectedTagIds = [];
    this.filterSelectedTagIds = [];
    this.selectedTagNames = [];
    this.assignedTagList = [];
    this.tripDetailsForm?.reset();
    this.durationControl?.setValue(null);
    this.unSelectAllTag = true;
    setTimeout(() => {
      this.initializePreAssignedTags();
      this.unSelectAllTag = false;
    }, 100);
    this.calculateDateRange('Past 7 days');
    this.addBotMessage(this.translate.instant('You can select a predefined question or type your own.'));
  }
}
